<template>
  <main class="overall-assessment-page">
    <DangerSignBar :show-stop="false" />
    <overall-assessment-header
      v-if="stage !== 'complete'"
      :patient="patientInfo"
      :active-tab="activeTab"
      :stage="stage"
      :has-no-risk="hasNoRisk"
      :has-visited-care-plan="hasVisitedCarePlan"
      @review-data="showReviewData = true"
    />

    <section
      ref="assessmentContent"
      class="overall-assessment-content"
      @scroll="updateScrollControls"
    >
      <template v-if="stage === 'assessment'">
        <section v-show="activeTab === 'Risk Assessment'" class="overall-assessment-tab-content">
          <OverallRiskAssessment />
        </section>

        <section v-show="activeTab === 'Care Plan'">
          <OverallRiskAssessmentCarePlan
            :key="carePlanRenderKey"
            :copy-identifiers="copyIdentifiers"
          />
        </section>
      </template>

      <SaveOrDiscardStep
        v-else-if="stage === 'finalize'"
        :show-discard-dialog="showDiscardDialog"
        :is-saving="isSaving"
        :save-failed="saveFailed"
        :auto-save="autoSave"
        @save="handleSaveResults"
        @discard="handleDiscardRequest"
        @discard-decision="handleDiscardDecision"
      />

      <AssessmentCompleteStep
        v-else
        @new-assessment="handleNewAssessment"
        @return-home="handleReturnHome"
      />
    </section>

    <overall-assessment-footer
      v-if="stage === 'assessment' && assessmentReady"
      :active-tab="activeTab"
      :has-no-risk="hasNoRisk"
      :auto-save="autoSave"
      @next-tab="handleNextFromRiskAssessment"
      @back-tab="onTabChange('Risk Assessment')"
      @finalize-save="handleFinalizeSave"
    />

    <div
      v-if="stage === 'assessment' && (showScrollTop || showScrollBottom)"
      class="overall-scroll-controls"
    >
      <button
        v-if="showScrollTop"
        type="button"
        class="overall-scroll-control"
        :aria-label="$t('app.navigation.scroll_to_top')"
        @click="scrollContentTo('top')"
      >
        <v-icon small>{{ icons.mdiArrowUp }}</v-icon>
      </button>
      <button
        v-if="showScrollBottom"
        type="button"
        class="overall-scroll-control"
        :aria-label="$t('app.navigation.scroll_to_bottom')"
        @click="scrollContentTo('bottom')"
      >
        <v-icon small>{{ icons.mdiArrowDown }}</v-icon>
      </button>
    </div>

    <ReviewDataModal
      v-model="showReviewData"
      :patient="patientInfo"
      :categories="reviewEvidenceCategories"
      :copy-identifiers="copyIdentifiers"
    />

    <v-dialog
        v-model="showFinalizeDialog"
        max-width="440px"
        overlay-color="#0f1e26"
        overlay-opacity="0.6"
    >
      <v-card class="referral-dialog">
        <button
          type="button"
          class="referral-dialog__back"
          :aria-label="$t('app.assessment_mode.coming_soon.back')"
          @click="handleReferralDismiss"
        >
          <v-icon small>{{ icons.mdiChevronLeft }}</v-icon>
        </button>
        <div class="referral-dialog__icon-wrapper">
          <div class="referral-dialog__icon-circle">
            <v-icon color="#2B8A8F" size="34">{{ icons.mdiCheck }}</v-icon>
          </div>
        </div>
        <v-card-title class="referral-dialog__title">
          {{ $t('questions.encounter_patient_referred.title') }}
        </v-card-title>
        <v-card-text class="referral-dialog__description">
          {{ $t('questions.encounter_patient_referred.description') }}
        </v-card-text>
        <v-card-actions class="referral-dialog__actions">
          <v-btn class="referral-dialog__btn-no" outlined @click="handleReferralAnswer('no')">
            {{ $t('responses.generic.no.title') }}
          </v-btn>
          <v-btn class="referral-dialog__btn-yes" @click="handleReferralAnswer('yes')">
            {{ $t('responses.generic.yes.title') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </main>
</template>

<script lang="ts">
import '../styles/overall-assessment.css'
import '@/features/text-mode/styles/text-mode.css'
import OverallAssessmentHeader from "../components/OverallAssessmentHeader.vue";
import OverallAssessmentFooter from "../components/OverallAssessmentFooter.vue";
import OverallRiskAssessment from "../components/risk-assessment/RiskAssessment.vue";
import ReviewDataModal from "../components/ReviewDataModal.vue";
import SaveOrDiscardStep from "../components/finalize/SaveOrDiscardStep.vue";
import AssessmentCompleteStep from "../components/finalize/AssessmentCompleteStep.vue";

import { defineComponent } from "vue";
import icons from "@/mixins/icons";
import OverallRiskAssessmentCarePlan from "@/features/overall-assessment/components/care-plan/CarePlan.vue";
import { useOverallAssessmentWorkflow } from "../composables/useOverallAssessmentWorkflow";
import DangerSignBar from "@/features/danger-signs/components/DangerSignBar.vue";
import type { OverallAssessmentWorkflow } from "../composables/useOverallAssessmentWorkflow";

function isScrollContainer(element: HTMLElement): boolean {
  const { overflowY } = window.getComputedStyle(element);
  return overflowY === 'auto' || overflowY === 'scroll';
}

function getScrollElement(content: HTMLElement): HTMLElement {
  if (isScrollContainer(content)) return content;
  return (document.scrollingElement as HTMLElement | null) || document.documentElement;
}

export default defineComponent( {
  name: 'OverallAssessmentPage',
  mixins: [icons],
  data() {
    return {
      showScrollTop: false,
      showScrollBottom: false,
    };
  },
  components: {
    OverallRiskAssessmentCarePlan,
    OverallRiskAssessment,
    OverallAssessmentHeader,
    OverallAssessmentFooter,
    ReviewDataModal,
    SaveOrDiscardStep,
    AssessmentCompleteStep,
    DangerSignBar,
  },
  setup(): OverallAssessmentWorkflow {
    return useOverallAssessmentWorkflow();
  },
  mounted() {
    window.addEventListener('resize', this.updateScrollControls);
    window.addEventListener('scroll', this.updateScrollControls, { passive: true });
    this.$nextTick(this.updateScrollControls);
  },
  updated() {
    this.$nextTick(this.updateScrollControls);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateScrollControls);
    window.removeEventListener('scroll', this.updateScrollControls);
  },
  beforeRouteLeave(_to, _from, next) {
    this.clearWorkflowContext();
    next();
  },
  methods: {
    updateScrollControls(): void {
      const content = this.$refs.assessmentContent as HTMLElement | undefined;
      if (!content) return;

      const scrollEl = getScrollElement(content);
      const nextShowScrollTop = scrollEl.scrollTop > 1;
      const nextShowScrollBottom = scrollEl.scrollTop + scrollEl.clientHeight < scrollEl.scrollHeight - 1;

      if (this.showScrollTop !== nextShowScrollTop) {
        this.showScrollTop = nextShowScrollTop;
      }
      if (this.showScrollBottom !== nextShowScrollBottom) {
        this.showScrollBottom = nextShowScrollBottom;
      }
    },
    scrollContentTo(position: 'top' | 'bottom'): void {
      const content = this.$refs.assessmentContent as HTMLElement | undefined;
      if (!content) return;

      const scrollEl = getScrollElement(content);
      scrollEl.scrollTo({
        top: position === 'top' ? 0 : scrollEl.scrollHeight,
        behavior: 'smooth',
      });
    },
  },
})
</script>
